import * as React from "react";
import { cn } from "../lib/utils";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Error message — wires aria-invalid + aria-describedby automatically when an id exists. */
  error?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, error, id, "aria-describedby": describedBy, ...props }, ref) => {
    const invalid = Boolean(error);
    return (
      <>
        <input
          type={type}
          id={id}
          ref={ref}
          aria-invalid={invalid || undefined}
          aria-describedby={error && id ? `${id}-error` : describedBy}
          className={cn(
            "flex h-9 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50",
            invalid && "border-destructive focus-visible:ring-destructive",
            className,
          )}
          {...props}
        />
        {error ? (
          <p
            id={id ? `${id}-error` : undefined}
            role="alert"
            className="mt-1 text-xs font-medium text-destructive"
          >
            {error}
          </p>
        ) : null}
      </>
    );
  },
);
Input.displayName = "Input";

export interface InputGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Icon element rendered at the left edge. Always aria-hidden by the group. */
  icon?: React.ReactNode;
  /** Addon rendered at the right edge (buttons, units, clear controls). */
  addon?: React.ReactNode;
}

const InputGroup = React.forwardRef<HTMLDivElement, InputGroupProps>(
  ({ className, icon, addon, children, ...props }, ref) => (
    <div
      ref={ref}
      role="group"
      className={cn(
        "relative flex items-center rounded-lg border border-input bg-background focus-within:ring-2 focus-within:ring-ring",
        className,
      )}
      {...props}
    >
      {icon ? (
        <span aria-hidden="true" className="pointer-events-none absolute left-3 flex text-muted-foreground">
          {icon}
        </span>
      ) : null}
      {children}
      {addon ? (
        <span className="absolute right-2 flex items-center text-muted-foreground">{addon}</span>
      ) : null}
    </div>
  ),
);
InputGroup.displayName = "InputGroup";

export { Input, InputGroup };
